import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import {
    Box,
    Typography,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Divider,
    Avatar,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import PeopleIcon from '@mui/icons-material/People';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUICustomDataGrid } from '../../../atomic/molecules';
import { SCUIButton } from '../../../atomic/atoms';
import RoleApprovalQueryService from '../../../services/query/roleApproval/RoleApprovalQuery.services';
import RoleApprovalCommandService from '../../../services/command/roleApproval/RoleApprovalCommand.services';
import Swal from 'sweetalert2';
import { RejectionReasonModal } from './components/RejectionReasonModal';
import {
    getCurrentApproverUser,
    getPendingRequestRowClassName,
    getRoleStatusInfo,
    isPendingRoleStatus,
} from './pendingRoleRequests.helpers';

interface PendingRequest {
    requestId: number;
    requestedUserId: number;
    requestedUserUsername: string;
    requestedUserEmail: string;
    requestedRoleId: number;
    requestedRoleName: string;
    requestedBy: number;
    requestedByUsername: string;
    assignedApproverUserId: number;
    assignedApproverUsername: string | null;
    approverUsernames: string[];
    status: number | string;
    statusText: string;
    approvalNotes?: string | null;
    requestNote?: string;
    requestDate: string;
    approvalDate?: string | null;
    updateDate?: string | null;
    updatedBy?: number | null;
    requestedUserHref: number;
}

interface StaffsResp {
    staffRef: number;
    staffAppointmentRef: number;
    appointmentTypeRef: number;
    personRef: number;
    personName: string;
    positionRef: number;
    positionName: string;
    organizationUnitRef: number;
    organizationUnitName: string;
    organizationUnitCode: string;
    organizationUnitTypeRef: number;
}

export const PendingRoleRequests: FC = () => {
    const { t, i18n } = useTranslation();
    const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedRequest, setSelectedRequest] = useState<PendingRequest | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [staffInfo, setStaffInfo] = useState<StaffsResp[]>([]);
    const [loadingStaffInfo, setLoadingStaffInfo] = useState(false);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [approversModalOpen, setApproversModalOpen] = useState(false);
    const [selectedApprovers, setSelectedApprovers] = useState<string[]>([]);
    const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
    const [rejectionTargetRequestId, setRejectionTargetRequestId] = useState<number | null>(null);
    const [isBulkRejection, setIsBulkRejection] = useState(false);

    const { userId: currentUserId, username: currentUsername } = getCurrentApproverUser();

    const fetchStaffInfo = async (userRef: number) => {
        setLoadingStaffInfo(true);
        try {
            const response = await RoleApprovalQueryService.GetStaffInfo(userRef);
            if (response && Array.isArray(response)) {
                setStaffInfo(response);
            } else {
                setStaffInfo([]);
            }
        } catch (error) {
            console.error('Error fetching staff info:', error);
            setStaffInfo([]);
            Swal.fire({
                title: t('common.error'),
                text: t('roleApproval.errorFetchingUserInfo'),
                icon: 'error',
            });
        } finally {
            setLoadingStaffInfo(false);
        }
    };

    const columns: ExtendedGridColDef[] = [
        {
            field: 'requestedUserUsername',
            headerName: t('roleApproval.requestedUser'),
            flex: 1,
            minWidth: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    params.row.requestedUserUsername ||
                    `${t('roleApproval.userIdLabel')} ${params.row.requestedUserId}` ||
                    '-'
                );
            },
        },
        {
            field: 'requestedRoleName',
            headerName: t('roleApproval.requestedRole'),
            flex: 1,
            minWidth: 150,
            sortable: false,
        },
        {
            field: 'requestDate',
            headerName: t('roleApproval.requestDate'),
            flex: 1,
            minWidth: 160,
            sortable: false,
            renderCell: (params) => {
                const date = new Date(params.value);
                return date.toLocaleString(i18n.language, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            },
        },
        {
            field: 'assignedApproverUsername',
            headerName: t('roleApproval.assignedApprover'),
            flex: 1,
            minWidth: 150,
            sortable: false,
            renderCell: (params) => {
                const approverName = params.value;
                if (!approverName || approverName === null) {
                    return (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            {t('general.notAssigned')}
                        </Typography>
                    );
                }
                return <Typography variant="body2">{approverName}</Typography>;
            },
        },
        {
            field: 'approverUsernames',
            headerName: t('roleApproval.allApprovers'),
            flex: 0.8,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => {
                const approvers = params.value as string[] | undefined;
                if (!approvers || approvers.length === 0) {
                    return (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            -
                        </Typography>
                    );
                }
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            '&:hover': {
                                opacity: 0.8,
                            },
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedApprovers(approvers);
                            setApproversModalOpen(true);
                        }}
                    >
                        <Chip
                            icon={<PeopleIcon fontSize="small" />}
                            label={`${approvers.length} ${t('roleApproval.approvers')}`}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: 'info.lighter',
                                },
                            }}
                        />
                    </Box>
                );
            },
        },
        {
            field: 'status',
            headerName: t('roleApproval.roleStatus'),
            flex: 0.6,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => {
                const { statusText, statusColor } = getRoleStatusInfo(params.value);

                return <Chip label={t(`roleApproval.status.${statusText}`)} color={statusColor} size="small" />;
            },
        },
        {
            field: 'action',
            headerName: t('general.action'),
            flex: 0.4,
            minWidth: 80,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRequest(params.row);
                                setModalOpen(true);
                                if (params.row.requestedUserHref) {
                                    fetchStaffInfo(params.row.requestedUserHref);
                                }
                            }}
                            sx={{
                                color: 'info.main',
                                '&:hover': {
                                    backgroundColor: 'info.lighter',
                                },
                            }}
                            title={t('roleApproval.viewRequestDetails')}
                        >
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Box>
                );
            },
        },
    ];

    const fetchPendingRequests = async () => {
        setLoading(true);
        try {
            const response = await RoleApprovalQueryService.GetPendingRoleRequestsByApprover({
                ApproverUserId: currentUserId,
                PageNumber: pageNumber,
                PageSize: pageSize,
            });

            if (response?.items && Array.isArray(response.items)) {
                const newItems = [...response.items];
                setPendingRequests(newItems);
                setTotalCount(response.totalCount || 0);
            } else {
                setPendingRequests([]);
                setTotalCount(0);
            }
        } catch {
            setPendingRequests([]);
            setTotalCount(0);
            Swal.fire(t('general.error'), t('roleApproval.errorFetchingRequests'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleApproveReject = async (requestId: number, status: 'Approved' | 'Rejected') => {
        if (status === 'Rejected') {
            setRejectionTargetRequestId(requestId);
            setIsBulkRejection(false);
            setRejectionModalOpen(true);
            return;
        }

        const result = await Swal.fire({
            title: t('general.accept'),
            text: t('roleApproval.confirmApproved'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('general.yes'),
            cancelButtonText: t('general.cancel'),
        });

        if (result.isConfirmed) {
            try {
                await RoleApprovalCommandService.ApproveRejectUserRoleRequest({
                    RequestId: requestId,
                    Status: 1,
                    ApprovalNotes: '',
                    UpdatedBy: currentUserId,
                });

                Swal.fire(t('general.success'), t('roleApproval.approvedSuccess'), 'success');
                fetchPendingRequests();
            } catch (error) {
                console.error('Error approving request:', error);
                Swal.fire(t('general.error'), t('roleApproval.errorProcessingRequest'), 'error');
            }
        }
    };

    const handleRejectConfirm = async (reason: string) => {
        setRejectionModalOpen(false);

        if (isBulkRejection) {
            try {
                const requests = selectedRows.map((requestId) => ({
                    requestId: requestId,
                    status: 2,
                    approvalNotes: '',
                    rejectionReason: reason,
                    updatedBy: currentUserId,
                }));

                await RoleApprovalCommandService.BulkApproveRejectUserRoleRequest({
                    requests: requests,
                });

                Swal.fire(
                    t('general.success'),
                    t('roleApproval.bulkRejectedSuccess', { count: selectedRows.length }),
                    'success'
                );

                setSelectedRows([]);
                fetchPendingRequests();
            } catch {
                Swal.fire(t('general.error'), t('roleApproval.errorBulkProcessing'), 'error');
            }
        } else if (rejectionTargetRequestId !== null) {
            try {
                await RoleApprovalCommandService.ApproveRejectUserRoleRequest({
                    RequestId: rejectionTargetRequestId,
                    Status: 2,
                    ApprovalNotes: '',
                    RejectionReason: reason,
                    UpdatedBy: currentUserId,
                });

                Swal.fire(t('general.success'), t('roleApproval.rejectedSuccess'), 'success');
                fetchPendingRequests();
            } catch (error) {
                console.error('Error rejecting request:', error);
                Swal.fire(t('general.error'), t('roleApproval.errorProcessingRequest'), 'error');
            }
        }

        setRejectionTargetRequestId(null);
        setIsBulkRejection(false);
    };

    const handleBulkApproveReject = async (status: 'Approved' | 'Rejected') => {
        if (selectedRows.length === 0) {
            Swal.fire(t('general.warning'), t('roleApproval.selectRequestsFirst'), 'warning');
            return;
        }

        if (status === 'Rejected') {
            setIsBulkRejection(true);
            setRejectionTargetRequestId(null);
            setRejectionModalOpen(true);
            return;
        }

        const result = await Swal.fire({
            title: t('roleApproval.bulkApprove'),
            text: t('roleApproval.confirmBulkApproved', { count: selectedRows.length }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('general.yes'),
            cancelButtonText: t('general.cancel'),
        });

        if (result.isConfirmed) {
            try {
                const requests = selectedRows.map((requestId) => ({
                    requestId: requestId,
                    status: 1,
                    approvalNotes: '',
                    updatedBy: currentUserId,
                }));

                await RoleApprovalCommandService.BulkApproveRejectUserRoleRequest({
                    requests: requests,
                });

                Swal.fire(
                    t('general.success'),
                    t('roleApproval.bulkApprovedSuccess', { count: selectedRows.length }),
                    'success'
                );

                setSelectedRows([]);
                fetchPendingRequests();
            } catch {
                Swal.fire(t('general.error'), t('roleApproval.errorBulkProcessing'), 'error');
            }
        }
    };

    const handlePageChange = (newPage: number) => {
        setPageNumber(newPage + 1);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPageNumber(1);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedRequest(null);
        setStaffInfo([]);
        setLoadingStaffInfo(false);
    };

    const handleSelectionModelChange = (model: any[]) => {
        // We only need the selected request IDs for bulk actions.
        setSelectedRows(model as number[]);
    };

    useEffect(() => {
        fetchPendingRequests();
    }, [pageNumber, pageSize]);

    return (
        <SCUIContent>
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    {t('roleApproval.pendingRequests')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {t('roleApproval.pendingRequestsDescription')}
                </Typography>

                <Box
                    sx={{
                        minHeight: selectedRows.length > 0 ? 'auto' : '0px',
                        mb: 2,
                        transition: 'all 0.3s ease-in-out',
                        overflow: 'hidden',
                    }}
                >
                    {selectedRows.length > 0 && (
                        <Box
                            sx={{
                                p: 2,
                                backgroundColor: 'primary.lighter',
                                borderRadius: 1,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                animation: 'fadeIn 0.3s ease-in-out',
                                '@keyframes fadeIn': {
                                    from: {
                                        opacity: 0,
                                        transform: 'translateY(-10px)',
                                    },
                                    to: {
                                        opacity: 1,
                                        transform: 'translateY(0)',
                                    },
                                },
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {t('roleApproval.selectedCount', { count: selectedRows.length })}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <SCUIButton
                                    type="error"
                                    sizeType="default"
                                    size="s"
                                    text={t('roleApproval.bulkReject')}
                                    showStartIcon={true}
                                    startIcon={<CloseIcon sx={{ color: 'white' }} />}
                                    onClick={() => handleBulkApproveReject('Rejected')}
                                    style={{
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                    }}
                                />
                                <SCUIButton
                                    type="primary"
                                    sizeType="default"
                                    size="s"
                                    text={t('roleApproval.bulkApprove')}
                                    showStartIcon={true}
                                    startIcon={<CheckIcon sx={{ color: 'white' }} />}
                                    onClick={() => handleBulkApproveReject('Approved')}
                                    style={{
                                        backgroundColor: '#4caf50',
                                        borderColor: '#4caf50',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>

                <SCUICustomDataGrid
                    rows={pendingRequests}
                    columns={columns}
                    loading={loading}
                    getRowId={(row: any) => row.requestId}
                    checkboxSelection
                    selectionOnClick={true}
                    onSelectionModelChange={handleSelectionModelChange}
                    selectionModel={selectedRows}
                    getRowClassName={(params: any) =>
                        getPendingRequestRowClassName(params.row, currentUsername)
                    }
                    pagination
                    paginationModeChange={true}
                    rowCount={totalCount}
                    page={pageNumber - 1}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    autoHeight
                    rowHeight={60}
                    sx={{
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                        '& .MuiDataGrid-row.warning-row': {
                            backgroundColor: 'rgba(255, 193, 7, 0.25) !important',
                            borderLeft: '5px solid #ffc107 !important',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 193, 7, 0.35) !important',
                            },
                        },
                        '& .warning-row .MuiDataGrid-cell': {
                            backgroundColor: 'inherit !important',
                        },
                        '& [data-id] .MuiDataGrid-row.warning-row': {
                            backgroundColor: 'rgba(255, 193, 7, 0.25) !important',
                        },
                        '& .MuiDataGrid-virtualScroller .MuiDataGrid-row.warning-row': {
                            backgroundColor: 'rgba(255, 193, 7, 0.25) !important',
                        },
                        '& .MuiDataGrid-cell': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                        },
                        '& .MuiDataGrid-cell[data-field="actions"]': {
                            justifyContent: 'center',
                        },
                        '& .MuiDataGrid-cell[data-field="details"]': {
                            justifyContent: 'center',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        },
                    }}
                />
            </Box>

            <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, position: 'relative' }}>
                    {t('roleApproval.requestDetailsModal.title')}
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseModal}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedRequest && (
                        <Box sx={{ p: 2 }}>
                            {/* User Information */}
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                                {t('roleApproval.requestDetailsModal.userInformation')}
                            </Typography>
                            <Box sx={{ mb: 3, pl: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>{t('roleApproval.requestedUser')}:</strong>{' '}
                                    {selectedRequest.requestedUserUsername ||
                                        `${t('roleApproval.userIdLabel')} ${selectedRequest.requestedUserId}` ||
                                        '-'}
                                </Typography>

                                {loadingStaffInfo ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                                        <CircularProgress size={16} />
                                        <Typography variant="body2" color="text.secondary">
                                            {t('roleApproval.requestDetailsModal.userInfoLoading')}
                                        </Typography>
                                    </Box>
                                ) : staffInfo.length > 0 ? (
                                    <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            {t('roleApproval.requestDetailsModal.userDetailsTitle')}
                                        </Typography>
                                        {staffInfo.map((staff, index) => (
                                            <Box key={index} sx={{ mb: index < staffInfo.length - 1 ? 2 : 0 }}>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>{t('roleApproval.requestDetailsModal.fullName')}</strong>{' '}
                                                    {staff.personName}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>{t('roleApproval.requestDetailsModal.position')}</strong>{' '}
                                                    {staff.positionName}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>
                                                        {t('roleApproval.requestDetailsModal.organizationUnit')}
                                                    </strong>{' '}
                                                    {staff.organizationUnitName} ({staff.organizationUnitCode})
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>{t('roleApproval.requestDetailsModal.staffRef')}</strong>{' '}
                                                    {staff.staffRef}
                                                </Typography>
                                                {index < staffInfo.length - 1 && <Divider sx={{ mt: 1 }} />}
                                            </Box>
                                        ))}
                                    </Box>
                                ) : null}
                            </Box>

                            {/* Request Information */}
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                                {t('roleApproval.requestDetailsModal.requestInformation')}
                            </Typography>
                            <Box sx={{ mb: 3, pl: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>{t('roleApproval.requestedRole')}:</strong>{' '}
                                    {selectedRequest.requestedRoleName}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>{t('roleApproval.requestDate')}:</strong> {selectedRequest.requestDate}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>{t('roleApproval.roleStatus')}:</strong>
                                    <Chip
                                        label={t(`roleApproval.status.${getRoleStatusInfo(selectedRequest.status).statusText}`)}
                                        color={getRoleStatusInfo(selectedRequest.status).statusColor}
                                        size="small"
                                        sx={{ ml: 1 }}
                                    />
                                </Typography>
                            </Box>

                            {/* Request Message */}
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                                {t('roleApproval.requestDetailsModal.requestMessage')}
                            </Typography>
                            <Box
                                sx={{
                                    pl: 2,
                                    p: 2,
                                    backgroundColor: 'grey.50',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ fontStyle: selectedRequest.requestNote ? 'normal' : 'italic' }}
                                >
                                    {selectedRequest.requestNote ||
                                        t('roleApproval.requestDetailsModal.noRequestMessage')}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 10, px: 3, py: 2 }}>
                    {/* Action buttons for pending requests */}
                    {selectedRequest && isPendingRoleStatus(selectedRequest.status) ? (
                                <>
                                    <SCUIButton
                                        type="error"
                                        sizeType="default"
                                        size="s"
                                        text={t('general.decline')}
                                        showStartIcon={true}
                                        startIcon={<CloseIcon sx={{ color: 'white' }} />}
                                        onClick={() => {
                                            handleApproveReject(selectedRequest.requestId, 'Rejected');
                                            handleCloseModal();
                                        }}
                                        style={{
                                            borderRadius: '8px',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            boxShadow: '0 2px 4px rgba(244, 67, 54, 0.3)',
                                            transition: 'all 0.2s ease-in-out',
                                            color: 'white',
                                        }}
                                    />

                                    <SCUIButton
                                        type="primary"
                                        sizeType="default"
                                        size="s"
                                        text={t('general.accept')}
                                        showStartIcon={true}
                                        startIcon={<CheckIcon sx={{ color: 'white' }} />}
                                        onClick={() => {
                                            handleApproveReject(selectedRequest.requestId, 'Approved');
                                            handleCloseModal();
                                        }}
                                        style={{
                                            backgroundColor: '#4caf50',
                                            borderColor: '#4caf50',
                                            borderRadius: '8px',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
                                            transition: 'all 0.2s ease-in-out',
                                            color: 'white',
                                        }}
                                    />
                                </>
                            ) : null}
                </DialogActions>
            </Dialog>

            {/* Approvers List Modal */}
            <Dialog
                open={approversModalOpen}
                onClose={() => setApproversModalOpen(false)}
                maxWidth="xs"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: 'hidden',
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        p: 3,
                        position: 'relative',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PeopleIcon sx={{ color: 'white', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                            {t('roleApproval.approversListModal.title')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 1 }}>
                        {t('roleApproval.approversListModal.description')}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={() => setApproversModalOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 12,
                            top: 12,
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.1)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    {selectedApprovers.length > 0 ? (
                        <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                            {selectedApprovers.map((approver, index) => {
                                const colors = [
                                    '#667eea',
                                    '#764ba2',
                                    '#f093fb',
                                    '#f5576c',
                                    '#4facfe',
                                    '#00f2fe',
                                    '#43e97b',
                                    '#38f9d7',
                                ];
                                const bgColor = colors[index % colors.length];

                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            p: 2,
                                            borderBottom: index < selectedApprovers.length - 1 ? '1px solid' : 'none',
                                            borderColor: 'divider',
                                            transition: 'background-color 0.2s',
                                            '&:hover': {
                                                backgroundColor: 'rgba(102, 126, 234, 0.04)',
                                            },
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                bgcolor: bgColor,
                                                fontSize: '1rem',
                                                fontWeight: 600,
                                                boxShadow: `0 4px 14px ${bgColor}40`,
                                            }}
                                        >
                                            {approver.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: 'text.primary',
                                                }}
                                            >
                                                {approver}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('roleApproval.approver')}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={`#${index + 1}`}
                                            size="small"
                                            sx={{
                                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                                color: '#667eea',
                                                fontWeight: 600,
                                                fontSize: '0.7rem',
                                            }}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <PeopleIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                {t('roleApproval.approversListModal.noApprovers')}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                {selectedApprovers.length > 0 && (
                    <Box
                        sx={{
                            p: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'grey.50',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {t('roleApproval.approversListModal.totalApprovers', { count: selectedApprovers.length })}
                        </Typography>
                        <SCUIButton
                            type="secondary"
                            sizeType="default"
                            size="s"
                            text={t('general.close')}
                            onClick={() => setApproversModalOpen(false)}
                            style={{
                                borderRadius: '8px',
                                fontWeight: 600,
                                textTransform: 'none',
                            }}
                        />
                    </Box>
                )}
            </Dialog>

            <RejectionReasonModal
                open={rejectionModalOpen}
                onClose={() => {
                    setRejectionModalOpen(false);
                    setRejectionTargetRequestId(null);
                    setIsBulkRejection(false);
                }}
                onConfirm={handleRejectConfirm}
                isBulk={isBulkRejection}
                selectedCount={selectedRows.length}
            />
        </SCUIContent>
    );
};
